import React, { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import "./style.css";
import GoogleLogo from "../../img/google_on_white.png";
import { CircularProgress } from "@mui/material";
import { ApiCallStatus } from "../../utils/constants";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_KEY;

const GOOGLE_MAPS_API_KEY_IS_USABLE =
  !!GOOGLE_MAPS_API_KEY &&
  GOOGLE_MAPS_API_KEY.trim() !== "" &&
  !GOOGLE_MAPS_API_KEY.includes("$(");

const MINIMUM_INPUT_LENGTH = 3;
const DEBOUNCE_MILLISECONDS = 600;

const ADDRESS_COMPONENT_TYPES = {
  streetNumber: "street_number",
  route: "route",
  sublocality: "sublocality",
  locality: "locality",
  province: "administrative_area_level_1",
  country: "country",
  postalCode: "postal_code",
};

const loadScript = (src, position, id) => {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
};

const toAddressComponents = (place) => {
  const components = place?.address_components ?? [];

  return Object.entries(ADDRESS_COMPONENT_TYPES).reduce(
    (result, [field, type]) => {
      const match = components.find((component) =>
        (component?.types ?? []).includes(type),
      );

      if (match) {
        result[field] = match.long_name;
      }

      return result;
    },
    {},
  );
};

const toPlaceDetails = (place) => {
  const addressComponents = toAddressComponents(place);
  const location = place?.geometry?.location;

  if (!location) {
    return addressComponents;
  }

  return {
    ...addressComponents,
    coordinates: { lat: location.lat(), lng: location.lng() },
  };
};

const autocompleteService = { current: null };

const GoogleAutoComplete = ({
  value,
  setValue,
  setPlaceDetails,
  setPlaceDetailsStatus,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const loaded = useRef(false);
  const sessionToken = useRef(null);
  const placesServiceNode = useRef(null);
  const placesService = useRef(null);
  const detailsRequestId = useRef(0);

  if (
    typeof window !== "undefined" &&
    GOOGLE_MAPS_API_KEY_IS_USABLE &&
    !loaded.current
  ) {
    if (!document.querySelector("#google-maps")) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
        document.querySelector("head"),
        "google-maps",
      );
    }

    loaded.current = true;
  }

  const currentSessionToken = () => {
    if (!sessionToken.current) {
      sessionToken.current =
        new window.google.maps.places.AutocompleteSessionToken();
    }

    return sessionToken.current;
  };

  const currentPlacesService = () => {
    if (!window.google?.maps?.places) {
      return null;
    }

    if (!placesServiceNode.current) {
      placesServiceNode.current = document.createElement("div");
    }

    if (!placesService.current) {
      placesService.current = new window.google.maps.places.PlacesService(
        placesServiceNode.current,
      );
    }

    return placesService.current;
  };

  const fetch = useMemo(
    () =>
      debounce((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, DEBOUNCE_MILLISECONDS),
    [],
  );

  const requestPlaceDetails = (placeId) => {
    const service = currentPlacesService();

    if (!service) {
      setPlaceDetailsStatus(ApiCallStatus.Failed);
      return;
    }

    const requestId = ++detailsRequestId.current;

    setLoading(true);
    setPlaceDetailsStatus(ApiCallStatus.InProgress);

    service.getDetails(
      {
        placeId,
        fields: ["address_components", "geometry"],
        sessionToken: currentSessionToken(),
      },
      (place, status) => {
        if (requestId !== detailsRequestId.current) {
          return;
        }

        setLoading(false);
        sessionToken.current = null;

        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !place
        ) {
          setPlaceDetailsStatus(ApiCallStatus.Failed);
          return;
        }

        setPlaceDetails(toPlaceDetails(place));
        setPlaceDetailsStatus(ApiCallStatus.Succeeded);
      },
    );
  };

  useEffect(
    () => () => {
      detailsRequestId.current = -1;
      fetch.cancel();
    },
    [fetch],
  );

  useEffect(() => {
    let active = true;

    if (!GOOGLE_MAPS_API_KEY_IS_USABLE) {
      return undefined;
    }

    if (!autocompleteService.current && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue.length < MINIMUM_INPUT_LENGTH) {
      fetch.cancel();
      setOptions(value ? [value] : []);
      setLoading(false);
      return undefined;
    }

    if (value && inputValue === value.description) {
      fetch.cancel();
      return undefined;
    }

    setLoading(true);

    fetch(
      {
        input: inputValue,
        sessionToken: currentSessionToken(),
        // componentRestrictions: {country: "za"},
        types: ["address"],
      },
      (results) => {
        if (active) {
          setLoading(false);

          let newOptions = [];

          if (value) {
            newOptions = [value];
          }

          if (results) {
            newOptions = [...newOptions, ...results];
          }

          setOptions(newOptions);
        }
      },
    );

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <>
      <Box sx={{ width: "100%", padding: "1rem 0" }}>
        <Grid container>
          <Grid item xs={12}>
            <Autocomplete
              id="address-autocomplete"
              sx={{ width: "100%" }}
              size="small"
              disabled={!GOOGLE_MAPS_API_KEY_IS_USABLE}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.description
              }
              filterOptions={(x) => x}
              options={options}
              autoComplete
              loading={loading}
              includeInputInList
              filterSelectedOptions
              value={value}
              noOptionsText="No locations"
              onChange={(event, newValue) => {
                setOptions(newValue ? [newValue, ...options] : options);
                setValue(newValue);

                if (newValue?.place_id) {
                  requestPlaceDetails(newValue.place_id);
                }
              }}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Address"
                  variant="outlined"
                  fullWidth
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const matches =
                  option.structured_formatting.main_text_matched_substrings ||
                  [];
                const parts = parse(
                  option.structured_formatting.main_text,
                  matches.map((match) => [
                    match.offset,
                    match.offset + match.length,
                  ]),
                );

                return (
                  <li {...props}>
                    <Grid container alignItems="center">
                      <Grid item sx={{ width: "100%", wordWrap: "break-word" }}>
                        {parts.map((part, index) => (
                          <Box
                            key={index}
                            component="span"
                            sx={{
                              fontWeight: part.highlight ? "bold" : "regular",
                            }}
                          >
                            {part.text}
                          </Box>
                        ))}
                        <Typography variant="body2" color="textSecondary">
                          {option.structured_formatting.secondary_text}
                        </Typography>
                      </Grid>
                    </Grid>
                  </li>
                );
              }}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "1rem",
          }}
        >
          <Typography
            variant="body2"
            className="google-font"
            sx={{ marginRight: "0.5rem" }}
          >
            Powered by
          </Typography>
          <Box
            component="img"
            src={GoogleLogo}
            alt="Google"
            sx={{ width: "50px" }}
          />
        </Box>
      </Box>
    </>
  );
};

export default GoogleAutoComplete;
